import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../store/hooks';
import { CText, CHeader } from '../../components/common';
import {
  FiCreditCard,
  FiDollarSign,
  FiTrendingUp,
  FiCheck,
  FiX,
  FiPlus,
  FiTrash2,
  FiClock,
  FiArrowUpRight,
} from 'react-icons/fi';
import axiosClient from '../../api/axios';

interface Transaction {
  id: string;
  type: 'earning' | 'payout';
  amount: number;
  description: string;
  date: string;
  status: 'completed' | 'pending' | 'failed';
}

interface PayoutAccount {
  id: string;
  type: 'bank' | 'upi';
  name: string;
  details: string;
  isPrimary: boolean;
}

const WalletAndPayouts: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);

  // Wallet state
  const [balance, setBalance] = useState(0);
  const [pendingBalance, setPendingBalance] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [payoutAccounts, setPayoutAccounts] = useState<PayoutAccount[]>([]);

  // Form state
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [accountType, setAccountType] = useState<'bank' | 'upi'>('bank');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [upiId, setUpiId] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch wallet data
  useEffect(() => {
    const fetchWalletData = async () => {
      setLoading(true);
      try {
        const response = await axiosClient.get('bookings/expert_wallet/');
        if (response.data) {
          setBalance(response.data.available_balance || 0);
          setPendingBalance(response.data.pending_balance || 0);
          setTotalEarnings(response.data.total_earnings || 0);
          setTransactions(response.data.transactions || []);
          setPayoutAccounts(response.data.payout_accounts || []);
        }
      } catch (err) {
        console.log('Wallet data not available');
        // Use mock data for display
        setBalance(12500);
        setPendingBalance(3000);
        setTotalEarnings(45000);
        setTransactions([
          { id: '1', type: 'earning', amount: 500, description: 'Video call with Rahul S.', date: '2026-01-10', status: 'completed' },
          { id: '2', type: 'earning', amount: 300, description: 'Chat session with Priya M.', date: '2026-01-09', status: 'completed' },
          { id: '3', type: 'payout', amount: 5000, description: 'Withdrawal to Bank', date: '2026-01-08', status: 'completed' },
          { id: '4', type: 'earning', amount: 500, description: 'Video call with Amit K.', date: '2026-01-07', status: 'pending' },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchWalletData();
  }, []);

  const handleAddAccount = async () => {
    if (accountType === 'bank') {
      if (!bankName || !accountNumber || !ifscCode || !accountHolderName) {
        setMessage({ type: 'error', text: 'Please fill all bank details' });
        return;
      }
    } else {
      if (!upiId) {
        setMessage({ type: 'error', text: 'Please enter UPI ID' });
        return;
      }
    }

    setSaving(true);
    setMessage(null);

    try {
      // Create Razorpay contact first
      const contactResponse = await axiosClient.post('bookings/create_payout_contact/', {
        name: accountHolderName || user?.name,
        email: user?.email,
        phone: user?.phone_number,
      });

      // Create fund account
      const fundAccountData = accountType === 'bank'
        ? {
            contact_id: contactResponse.data.contact_id,
            account_type: 'bank_account',
            bank_account: {
              name: accountHolderName,
              ifsc: ifscCode,
              account_number: accountNumber,
            },
          }
        : {
            contact_id: contactResponse.data.contact_id,
            account_type: 'vpa',
            vpa: {
              address: upiId,
            },
          };

      await axiosClient.post('bookings/create_fund_account/', fundAccountData);

      // Add to local state
      const newAccount: PayoutAccount = {
        id: Date.now().toString(),
        type: accountType,
        name: accountType === 'bank' ? bankName : 'UPI',
        details: accountType === 'bank' ? `****${accountNumber.slice(-4)}` : upiId,
        isPrimary: payoutAccounts.length === 0,
      };
      setPayoutAccounts((prev) => [...prev, newAccount]);

      // Reset form
      setShowAddAccount(false);
      setBankName('');
      setAccountNumber('');
      setIfscCode('');
      setAccountHolderName('');
      setUpiId('');

      setMessage({ type: 'success', text: 'Payout account added successfully!' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to add account' });
    } finally {
      setSaving(false);
    }
  };

  const handleWithdraw = async () => {
    if (balance < 500) {
      setMessage({ type: 'error', text: 'Minimum withdrawal amount is Rs.500' });
      return;
    }
    if (payoutAccounts.length === 0) {
      setMessage({ type: 'error', text: 'Please add a payout account first' });
      return;
    }

    setWithdrawing(true);
    setMessage(null);

    try {
      await axiosClient.post('bookings/request_payout/', {
        amount: balance,
      });

      setPendingBalance((prev) => prev + balance);
      setBalance(0);
      setMessage({ type: 'success', text: 'Withdrawal requested! Funds will be credited within 2-3 business days.' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to process withdrawal' });
    } finally {
      setWithdrawing(false);
    }
  };

  const removeAccount = async (accountId: string) => {
    try {
      await axiosClient.delete(`bookings/fund_account/${accountId}/`);
      setPayoutAccounts((prev) => prev.filter((a) => a.id !== accountId));
      setMessage({ type: 'success', text: 'Account removed' });
    } catch (err) {
      // Remove locally anyway for demo
      setPayoutAccounts((prev) => prev.filter((a) => a.id !== accountId));
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: colors.backgroundColor }}
      >
        <CText type="M14" color={colors.grayScale500}>
          Loading wallet...
        </CText>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="p-4 lg:p-8">
        <div className="lg:max-w-2xl lg:mx-auto">
          <CHeader title="Wallet & Payouts" />

          {/* Message */}
          {message && (
            <div
              className="mb-4 p-3 rounded-xl flex items-center gap-2"
              style={{
                backgroundColor: message.type === 'success' ? colors.green + '20' : colors.alertColor + '20',
              }}
            >
              {React.createElement(
                message.type === 'success' ? FiCheck : FiX as any,
                { size: 18, color: message.type === 'success' ? colors.green : colors.alertColor }
              )}
              <CText
                type="r14"
                color={message.type === 'success' ? colors.green : colors.alertColor}
              >
                {message.text}
              </CText>
            </div>
          )}

          {/* Balance Cards */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div
              className="rounded-2xl p-5"
              style={{
                background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                {React.createElement(FiDollarSign as any, { size: 18, color: 'rgba(255,255,255,0.8)' })}
                <CText type="r12" color="rgba(255,255,255,0.8)">
                  Available Balance
                </CText>
              </div>
              <CText type="B24" color="#fff">
                Rs.{balance.toLocaleString()}
              </CText>
            </div>
            <div
              className="rounded-2xl p-5"
              style={{
                backgroundColor: colors.cardBackground || colors.inputBackground,
                border: `1px solid ${colors.borderColor}`,
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                {React.createElement(FiClock as any, { size: 18, color: colors.grayScale500 })}
                <CText type="r12" color={colors.grayScale500}>
                  Pending
                </CText>
              </div>
              <CText type="B24" color={colors.textColor}>
                Rs.{pendingBalance.toLocaleString()}
              </CText>
            </div>
          </div>

          {/* Total Earnings */}
          <div
            className="rounded-xl p-4 mb-6 flex items-center justify-between"
            style={{
              backgroundColor: colors.green + '15',
              border: `1px solid ${colors.green}30`,
            }}
          >
            <div className="flex items-center gap-3">
              {React.createElement(FiTrendingUp as any, { size: 20, color: colors.green })}
              <div>
                <CText type="r12" color={colors.grayScale500}>
                  Total Earnings
                </CText>
                <CText type="B18" color={colors.green}>
                  Rs.{totalEarnings.toLocaleString()}
                </CText>
              </div>
            </div>
          </div>

          {/* Withdraw Button */}
          <button
            onClick={handleWithdraw}
            disabled={withdrawing || balance < 500}
            className="w-full py-4 rounded-2xl font-semibold mb-6 flex items-center justify-center gap-2"
            style={{
              background: balance >= 500 ? `linear-gradient(135deg, ${colors.primary}, #9F03F2)` : colors.grayScale400,
              color: '#fff',
              opacity: withdrawing ? 0.7 : 1,
            }}
          >
            {React.createElement(FiArrowUpRight as any, { size: 20, color: '#fff' })}
            {withdrawing ? 'Processing...' : `Withdraw Rs.${balance.toLocaleString()}`}
          </button>

          {/* Payout Accounts */}
          <div
            className="rounded-2xl p-5 mb-6"
            style={{
              backgroundColor: colors.cardBackground || colors.inputBackground,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {React.createElement(FiCreditCard as any, { size: 20, color: colors.primary })}
                <CText type="B16" color={colors.textColor}>
                  Payout Accounts
                </CText>
              </div>
              <button
                onClick={() => setShowAddAccount(!showAddAccount)}
                className="flex items-center gap-1 px-3 py-1 rounded-lg"
                style={{ backgroundColor: colors.primary + '15' }}
              >
                {React.createElement(FiPlus as any, { size: 14, color: colors.primary })}
                <CText type="M10" color={colors.primary}>
                  Add
                </CText>
              </button>
            </div>

            {/* Add Account Form */}
            {showAddAccount && (
              <div
                className="p-4 rounded-xl mb-4"
                style={{ backgroundColor: colors.backgroundColor }}
              >
                {/* Account Type Tabs */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setAccountType('bank')}
                    className="flex-1 py-2 rounded-lg text-sm font-medium"
                    style={{
                      backgroundColor: accountType === 'bank' ? colors.primary : 'transparent',
                      color: accountType === 'bank' ? '#fff' : colors.grayScale500,
                      border: accountType !== 'bank' ? `1px solid ${colors.borderColor}` : 'none',
                    }}
                  >
                    Bank Account
                  </button>
                  <button
                    onClick={() => setAccountType('upi')}
                    className="flex-1 py-2 rounded-lg text-sm font-medium"
                    style={{
                      backgroundColor: accountType === 'upi' ? colors.primary : 'transparent',
                      color: accountType === 'upi' ? '#fff' : colors.grayScale500,
                      border: accountType !== 'upi' ? `1px solid ${colors.borderColor}` : 'none',
                    }}
                  >
                    UPI
                  </button>
                </div>

                {accountType === 'bank' ? (
                  <>
                    <input
                      type="text"
                      value={accountHolderName}
                      onChange={(e) => setAccountHolderName(e.target.value)}
                      placeholder="Account Holder Name"
                      className="w-full px-4 py-3 rounded-xl mb-3 outline-none"
                      style={{
                        backgroundColor: colors.inputBackground,
                        color: colors.textColor,
                        border: `1px solid ${colors.borderColor}`,
                      }}
                    />
                    <input
                      type="text"
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      placeholder="Bank Name"
                      className="w-full px-4 py-3 rounded-xl mb-3 outline-none"
                      style={{
                        backgroundColor: colors.inputBackground,
                        color: colors.textColor,
                        border: `1px solid ${colors.borderColor}`,
                      }}
                    />
                    <input
                      type="text"
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                      placeholder="Account Number"
                      className="w-full px-4 py-3 rounded-xl mb-3 outline-none"
                      style={{
                        backgroundColor: colors.inputBackground,
                        color: colors.textColor,
                        border: `1px solid ${colors.borderColor}`,
                      }}
                    />
                    <input
                      type="text"
                      value={ifscCode}
                      onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                      placeholder="IFSC Code"
                      className="w-full px-4 py-3 rounded-xl mb-3 outline-none"
                      style={{
                        backgroundColor: colors.inputBackground,
                        color: colors.textColor,
                        border: `1px solid ${colors.borderColor}`,
                      }}
                    />
                  </>
                ) : (
                  <input
                    type="text"
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    placeholder="UPI ID (e.g., name@paytm)"
                    className="w-full px-4 py-3 rounded-xl mb-3 outline-none"
                    style={{
                      backgroundColor: colors.inputBackground,
                      color: colors.textColor,
                      border: `1px solid ${colors.borderColor}`,
                    }}
                  />
                )}

                <button
                  onClick={handleAddAccount}
                  disabled={saving}
                  className="w-full py-3 rounded-xl font-medium"
                  style={{
                    backgroundColor: colors.primary,
                    color: '#fff',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? 'Adding...' : 'Add Account'}
                </button>
              </div>
            )}

            {/* Account List */}
            {payoutAccounts.length > 0 ? (
              <div className="space-y-2">
                {payoutAccounts.map((account) => (
                  <div
                    key={account.id}
                    className="flex items-center justify-between p-3 rounded-xl"
                    style={{ backgroundColor: colors.backgroundColor }}
                  >
                    <div className="flex items-center gap-3">
                      {React.createElement(FiCreditCard as any, { size: 18, color: colors.grayScale500 })}
                      <div>
                        <CText type="M12" color={colors.textColor}>
                          {account.name}
                        </CText>
                        <CText type="r10" color={colors.grayScale500}>
                          {account.details}
                        </CText>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {account.isPrimary && (
                        <span
                          className="px-2 py-0.5 rounded text-xs"
                          style={{ backgroundColor: colors.primary + '20', color: colors.primary }}
                        >
                          Primary
                        </span>
                      )}
                      <button onClick={() => removeAccount(account.id)} className="p-2">
                        {React.createElement(FiTrash2 as any, { size: 14, color: colors.alertColor })}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <CText type="r12" color={colors.grayScale500} style={{ fontStyle: 'italic' }}>
                No payout accounts added yet
              </CText>
            )}
          </div>

          {/* Recent Transactions */}
          <div
            className="rounded-2xl p-5"
            style={{
              backgroundColor: colors.cardBackground || colors.inputBackground,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <CText type="B16" color={colors.textColor} style={{ marginBottom: 16 }}>
              Recent Transactions
            </CText>

            {transactions.length > 0 ? (
              <div className="space-y-3">
                {transactions.map((tx) => (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between p-3 rounded-xl"
                    style={{ backgroundColor: colors.backgroundColor }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{
                          backgroundColor: tx.type === 'earning' ? colors.green + '20' : colors.primary + '20',
                        }}
                      >
                        {React.createElement(
                          tx.type === 'earning' ? FiTrendingUp : FiArrowUpRight as any,
                          { size: 18, color: tx.type === 'earning' ? colors.green : colors.primary }
                        )}
                      </div>
                      <div>
                        <CText type="M12" color={colors.textColor}>
                          {tx.description}
                        </CText>
                        <CText type="r10" color={colors.grayScale500}>
                          {formatDate(tx.date)}
                        </CText>
                      </div>
                    </div>
                    <div className="text-right">
                      <CText
                        type="M12"
                        color={tx.type === 'earning' ? colors.green : colors.textColor}
                      >
                        {tx.type === 'earning' ? '+' : '-'}Rs.{tx.amount}
                      </CText>
                      <CText
                        type="r10"
                        color={tx.status === 'completed' ? colors.green : colors.alertColor}
                      >
                        {tx.status}
                      </CText>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <CText type="r12" color={colors.grayScale500} style={{ fontStyle: 'italic' }}>
                No transactions yet
              </CText>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletAndPayouts;
